/**
 * Colorful — the canonical 60 category cards.
 *
 * Each card carries:
 *   - a numeric id (`'01'` … `'60'`) printed in the bottom-left of
 *     the card,
 *   - a `theme` (the word in the bottom-right of the card),
 *   - 4 prompts indexed [round1, round2, round3, round4and5]. The
 *     fourth slot is shared by both round 4 and round 5 of a 6-player
 *     game (rulebook: "4-5)" spot).
 *
 * The card art lives at `public/games/colorful/card-NNN.webp`. Cards
 * 211–299 (odd) hold categories 01–45; cards 121–149 (odd) hold
 * categories 46–60. `cardFront` resolves the right path for each id.
 *
 * Categories transcribed verbatim from the V2 Colorful rulebook deck
 * (en, late 2020). Typos preserved where present on the physical
 * cards (e.g. "Sceintific" on card 40, "Colrful" on card 51).
 */

export type PromptSlot = 0 | 1 | 2 | 3   // index into prompts[]

export interface CategoryCard {
  id: string
  theme: string
  prompts: [string, string, string, string]
  /** Path to the front PNG. */
  cardFront: string
  /** Path to the uniform category-card back PNG. */
  cardBack: string
}

const CARD_PATH = '/games/colorful/'
const CATEGORY_BACK = `${CARD_PATH}card-122.webp`

/**
 * Resolve the card-image path for a category id (`'01'` … `'60'`).
 *   - Categories 01–45 → odd numbered file `card-(211 + (n-1)*2).webp`
 *   - Categories 46–60 → odd numbered file `card-(121 + (n-46)*2).webp`
 */
function categoryFront(id: string): string {
  const n = parseInt(id, 10)
  let fileNum: number
  if (n >= 1 && n <= 45) {
    fileNum = 211 + (n - 1) * 2
  } else if (n >= 46 && n <= 60) {
    fileNum = 121 + (n - 46) * 2
  } else {
    throw new Error(`Invalid category id: ${id}`)
  }
  return `${CARD_PATH}card-${String(fileNum).padStart(3, '0')}.webp`
}

function card(
  id: string,
  theme: string,
  prompts: [string, string, string, string],
): CategoryCard {
  return {
    id, theme, prompts,
    cardFront: categoryFront(id),
    cardBack:  CATEGORY_BACK,
  }
}

export const CATEGORIES: CategoryCard[] = [
  card('01', 'Plants',              ['vegetable', 'plant', 'flower', 'tree']),
  card('02', 'Animals',             ['animal', 'bird', 'fish', 'insect']),
  card('03', 'Time',                ['season', 'period in history', 'time', 'month']),
  card('04', 'Geo Science',         ['weather', 'planet', 'rock type', 'landscape']),
  card('05', 'Area',                ['building', 'country', 'continent', 'city']),
  card('06', 'Travel',              ['vehicle', 'car maker', 'airline', 'subway']),
  card('07', 'Amusement',           ['amusement park', 'celebration', 'festival', 'show']),
  card('08', 'Food',                ['food', 'drink', 'seasoning', 'flavor']),
  card('09', 'Cuisine',             ['local food', 'American food', 'Chinese food', 'Japanese food']),
  card('10', 'Meals',               ['breakfast', 'lunch', 'dinner', 'snack']),
  card('11', 'Senses',              ['a feeling', 'a texture', 'a taste', 'a smell']),
  card('12', 'Food Establishments', ['fast food', 'restaurant', 'cafe/bar', 'sweets shop']),
  card('13', 'Celebrity',           ['artist', 'athlete', 'author', 'actor']),
  card('14', 'Entertainment',       ['team', 'movie', 'band', 'TV show']),
  card('15', 'Names',               ['any name', 'pet name', 'old name', 'common name']),
  card('16', 'Time Period',         ["2010's", "1990's", "1980's", "1950's"]),
  card('17', 'Jobs',                ['job title', 'specialty', 'labor', 'a CEO']),
  card('18', 'Shopping',            ['clothing item', 'currency bill', 'store', 'grocer']),
  card('19', 'Vehicle',             ['vehicle', 'car', 'airplane', 'boat']),
  card('20', 'School',              ['a school', 'subject', 'classroom object', 'scientist']),
  card('21', 'Tools',               ['technology item', 'wood working', 'stationary', 'any tool']),
  card('22', 'Objects',             ['common item', 'fictional item', 'rare item', 'famous object']),
  card('23', 'Clothing',            ['accessory', 'fabric', 'cosmetics', 'item of clothing']),
  card('24', 'Materials',           ['liquid', 'solid', 'gas', 'invisible thing']),
  card('25', 'Descriptors',         ['light', 'heavy', 'round', 'sharp']),
  card('26', 'Characteristics',     ['long', 'shiny', 'stretchy', 'flat']),
  card('27', 'More Animals',        ['furry animal', 'smooth animal', 'silly animal', 'extinct animal']),
  card('28', 'Sports',              ['ball sport', 'famous sport', 'uncommon sport', 'olympic sport']),
  card('29', 'Music',               ['genre of music', 'musical instrument', 'famous musician', 'song']),
  card('30', 'Subculture',          ['subculture', 'series', 'monster movie', 'taboo']),
  card('31', 'Characters',          ['fairy tale character', 'film character', 'animated character', 'cultural character']),
  card('32', 'Titles',              ['a film', 'a novel', 'a story', 'a music album']),
  card('33', 'Fantasy',             ['monster', 'hero', 'villain', 'magical']),
  card('34', 'Words',               ['a word that starts with "K"', 'a word that starts with "H"', 'a word that starts with "Y"', 'a four letter word']),
  card('35', 'Experiences',         ['joy', 'nostalgia', 'laughter', 'boredom']),
  card('36', 'Language',            ['a word', 'a letter or a number', 'a language', 'a sound']),
  card('37', 'Consumables',         ['edible', 'skin care', 'container', 'plastic']),
  card('38', 'Preference',          ['something you fear', 'something you enjoy', 'something you give away', 'something you are']),
  card('39', 'Games',               ['classic game', 'video game', 'card game', 'board game']),
  card('40', 'Science',             ['invention', 'material', 'sceintific discovery', 'a scientist']),
  card('41', 'Actions',             ['action done in the morning', 'action done before bed', 'action done in public', 'action done in private']),
  card('42', 'Words',               ['noun', 'verb', 'adjective', 'a praise']),
  card('43', 'Imagination',         ['imaginary animal', 'imaginary place', 'two words mixed together', 'imaginary creature']),
  card('44', 'Folklore',            ['folktale', 'myth', 'conspiracy', 'a ritual']),
  card('45', 'Gatherings',          ['event', 'natural phenomenon', 'gathering', 'crowded place']),
  card('46', 'Celebrities',         ['singer', 'TV star', 'pop star', 'politician']),
  card('47', 'Candy',               ['soft candy', 'hard candy', 'round candy', 'long candy']),
  card('48', 'Snacks',              ['chips', 'cookies', 'cake', 'drink']),
  card('49', 'Nature',              ['flower', 'fish', 'mammal', 'organism']),
  card('50', 'Occupations',         ['occupation', 'job title', 'chef tool', 'farm tool']),
  card('51', 'Herbs',               ['sweet herb', 'strong herb', 'bitter herb', 'colrful herb']),
  card('52', 'Scents',              ['strong scent', 'sweet scent', 'foul scent', 'nice scent']),
  card('53', 'Art',                 ['painting', 'sculpture', 'art form', 'art tool']),
  card('54', 'Clothes',             ['shoe brand', 'shirt brand', 'item of clothing', 'fabric type']),
  card('55', 'Culture',             ['cultural item', 'cultural celebration', 'dress style', 'type of uniform']),
  card('56', 'Architecture',        ['a building', 'architectural style', 'old place', 'new place']),
  card('57', 'Travel',              ['city', 'travel brand', 'currency', 'nation']),
  card('58', 'Tastes',              ['citrus', 'bitter', 'herbal', 'sour']),
  card('59', 'Design',              ['brand of razor', 'chair', 'Japanese design brand', 'designer']),
  card('60', 'Games',               ['video game', 'board game', 'RPG', 'abstract game']),
]
